<template>
    <Head title="Locations" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">Locations</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">Manage and organize your asset locations</p>
                    </div>
                    <InfoButton title="Locations Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            Locations organize your physical spaces into a hierarchical structure (sites, buildings, floors,
                            rooms, etc.). Each location can contain assets, have assigned users with access permissions, and
                            include child locations for nested organization. Locations support two view modes: Hierarchy
                            (tree view showing parent-child relationships) and List (flat table view with pagination).
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Statistics Cards:</strong> View Root Locations (top-level), All Locations (total count),
                              Active, and Inactive location counts<br>
                            • <strong>Search:</strong> Filter locations by name in real-time<br>
                            • <strong>View Toggle:</strong> Switch between Hierarchy (tree structure) and List (table with
                              pagination) views<br>
                            • <strong>Type Filter:</strong> Filter by location type (Building, Floor, Room, Site, Warehouse,
                              Other)<br>
                            • <strong>Status Filter:</strong> Show All, Active only, or Inactive only locations<br>
                            • <strong>Hierarchy View:</strong> Displays nested tree structure showing parent-child relationships
                              with expand/collapse controls<br>
                            • <strong>List View:</strong> Shows flat table with pagination, sortable columns, and quick access
                              to asset counts<br>
                            • <strong>New Location:</strong> Create new locations with address, type, and parent location
                              options
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Start with hierarchy</strong> - Organize from largest to smallest (Site → Building →
                              Floor → Room)<br>
                            • <strong>Use codes</strong> - Assign short codes (e.g., BLDG-01, FLR-02) for quick identification<br>
                            • <strong>Hierarchy vs List</strong> - Use Hierarchy for organization planning; use List for quick
                              searches and statistics<br>
                            • <strong>Active status</strong> - Inactive locations don't appear in asset assignment dropdowns<br>
                            • <strong>Asset tracking</strong> - The Assets column shows how many assets are at each location<br>
                            • <strong>User access</strong> - Assign users to locations for mobile access and asset filtering<br>
                            • <strong>Clear filters</strong> - Reset all filters to see complete location hierarchy
                        </p>
                    </InfoButton>
                </div>
                <Link
                    v-if="canUpdate"
                    :href="route('settings.locations.create')"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Location
                </Link>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <!-- Root Locations -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Root Locations</p>
                            <p class="text-2xl font-semibold text-purple-600 dark:text-purple-400">{{ stats.root_locations }}</p>
                        </div>
                    </div>
                </div>

                <!-- All Locations -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">All Locations</p>
                            <p class="text-2xl font-semibold text-blue-600 dark:text-blue-400">{{ stats.total_locations }}</p>
                        </div>
                    </div>
                </div>

                <!-- Active Locations -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Active Locations</p>
                            <p class="text-2xl font-semibold text-green-600 dark:text-green-400">{{ stats.active_locations }}</p>
                        </div>
                    </div>
                </div>

                <!-- Inactive Locations -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-md p-3">
                            <svg class="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Locations</p>
                            <p class="text-2xl font-semibold text-gray-600 dark:text-gray-400">{{ stats.inactive_locations }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters and View Toggle -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <div class="p-4 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <!-- Search -->
                        <div class="flex-1 max-w-lg">
                            <div class="relative">
                                <input
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search locations..."
                                    class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                    @input="debounceSearch"
                                />
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- View Toggle -->
                        <div class="flex items-center space-x-2">
                            <button
                                @click="changeView('hierarchy')"
                                :class="[
                                    'px-3 py-2 rounded-md text-sm font-medium',
                                    viewMode === 'hierarchy'
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                ]"
                            >
                                <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                Hierarchy
                            </button>
                            <button
                                @click="changeView('list')"
                                :class="[
                                    'px-3 py-2 rounded-md text-sm font-medium',
                                    viewMode === 'list'
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                ]"
                            >
                                <svg class="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                                </svg>
                                List
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Filter Pills -->
                <div class="p-4 flex flex-wrap gap-2">
                    <select
                        v-model="selectedFilters.type"
                        class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Types</option>
                        <option v-for="(label, value) in locationTypes" :key="value" :value="value">
                            {{ label }}
                        </option>
                    </select>

                    <select
                        v-model="selectedFilters.is_active"
                        class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Status</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </select>

                    <button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            <!-- Hierarchy View -->
            <div v-if="viewMode === 'hierarchy'" class="space-y-4">
                <!-- Show hierarchy tree if we have array data with items -->
                <template v-if="isHierarchyData && locations.length > 0">
                    <LocationTree
                        v-for="location in locations.filter(loc => loc !== null)"
                        :key="location.id"
                        :location="location"
                        :level="0"
                    />
                </template>

                <!-- Empty State - only show when we have array data but it's empty -->
                <div v-else-if="isHierarchyData && locations.length === 0" class="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No locations found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new location.</p>
                </div>
            </div>

            <!-- List View -->
            <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6">
                <!-- Table - show if we have list data with items -->
                <table v-if="isListData && locations.data && locations.data.length > 0" class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Location
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Type
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Address
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Assets
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Status
                            </th>
                            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                        <tr v-for="location in locations.data" :key="location.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td class="px-6 py-4">
                                <div>
                                    <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ location.name }}</div>
                                    <div class="text-sm text-gray-500 dark:text-gray-400">{{ location.code || 'No code' }}</div>
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                    {{ getTypeLabel(location.type) }}
                                </span>
                            </td>
                            <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                {{ location.city || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {{ location.assets_count || 0 }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="[
                                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                        location.is_active
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                    ]"
                                >
                                    {{ location.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <Link
                                    :href="route('settings.locations.show', location.id)"
                                    class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    View
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Empty State - only show when we have list data but it's empty -->
                <div v-else-if="isListData && (!locations.data || locations.data.length === 0)" class="p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No locations found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new location.</p>
                </div>

                <!-- Pagination -->
                <div v-if="isListData && locations.data && locations.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <button
                            v-if="locations.prev_page_url"
                            @click="goToPage(locations.current_page - 1)"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Previous
                        </button>
                        <button
                            v-if="locations.next_page_url"
                            @click="goToPage(locations.current_page + 1)"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Next
                        </button>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-300">
                                Showing
                                <span class="font-medium">{{ locations.from }}</span>
                                to
                                <span class="font-medium">{{ locations.to }}</span>
                                of
                                <span class="font-medium">{{ locations.total }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    v-for="(link, index) in locations.links"
                                    :key="index"
                                    @click="goToPageFromLink(link)"
                                    :disabled="!link.url || link.active"
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                        link.active
                                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400'
                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600',
                                        !link.url || link.active ? 'cursor-not-allowed opacity-50' : ''
                                    ]"
                                    v-html="link.label"
                                />
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import route from '@/Utils/route';
import LocationTree from '@/Components/Locations/LocationTree.vue';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    locations: [Object, Array],
    allLocations: Array,
    stats: Object,
    locationTypes: Object,
    filters: Object,
});

// Check permissions
const page = usePage();
const permissions = computed(() => page.props.auth?.permissions || []);
const canUpdate = computed(() => permissions.value.includes('system.settings.update'));

const viewMode = ref(props.filters.view || 'hierarchy');
const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    type: props.filters.type || '',
    is_active: props.filters.is_active || '',
});

let searchTimeout = null;

// Computed properties to handle different data structures
const isHierarchyData = computed(() => {
    return Array.isArray(props.locations);
});

const isListData = computed(() => {
    return props.locations && typeof props.locations === 'object' && 'data' in props.locations;
});

const hasActiveFilters = computed(() => {
    return selectedFilters.value.type ||
           selectedFilters.value.is_active ||
           searchQuery.value;
});

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const changeView = (view) => {
    viewMode.value = view;
    // When changing views, we need to fetch fresh data with the correct structure
    // Only include non-empty parameters
    const params = { view: view };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.type) params.type = selectedFilters.value.type;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.locations.index'), params, {
        preserveScroll: true,
        // Don't preserve state when changing views since data structure changes
    });
};

const applyFilters = () => {
    // Only include non-empty parameters
    const params = { view: viewMode.value };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.type) params.type = selectedFilters.value.type;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.locations.index'), params, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        type: '',
        is_active: '',
    };
    applyFilters();
};

const getTypeLabel = (type) => {
    return props.locationTypes[type] || type;
};

const goToPage = (page) => {
    // Only include non-empty parameters
    const params = { page, view: viewMode.value };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.type) params.type = selectedFilters.value.type;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.locations.index'), params, {
        preserveState: true,
        preserveScroll: true,
    });
};

const goToPageFromLink = (link) => {
    if (!link.url || link.active) return;

    // Extract page number from URL
    const url = new URL(link.url);
    const page = url.searchParams.get('page');

    if (page) {
        goToPage(page);
    }
};
</script>
